import React, { useState, useRef, useEffect, useCallback } from 'react'
import { filter, find, some, isArray } from 'lodash'
import PropTypes from 'prop-types'
import queryString from 'query-string'
import { withRouter } from 'react-router-dom'
import { IconDown, IconRight, CheckBox } from 'blocket-ui'
import { onEnterKeyEvent } from 'utils/onEnterKeyEvent'
import { texts } from 'utils/geo'
import { getInitialParams } from './getInitialParams'
import {
  StyledIconDown,
  StyledIconUp,
  StyledClearButton,
  StyledConfirmButton,
  StyledButtonText,
  StyledSubAreaLables,
} from './StyledMultiSelect'

export const MultiSelect = ({
  handleConfirm,
  placeholder,
  ariaLabel,
  topOptions,
  options,
  location,
  regionParameter,
  allOptions,
}) => {
  const initialState = getInitialParams(location, regionParameter, allOptions)
  const { r } = queryString.parse(location.search)

  const mergedOptions = [...topOptions, ...options]

  const [selectedOptions, setSelectedOptions] = useState(initialState)
  const [isOpen, setIsOpen] = useState(false)
  const [selectRect, setSelectRect] = useState({}) // Stores size and position of the select's dropdown
  const [initialHeight, setInitialHeight] = useState(0)
  const [placement, setPlacement] = useState('left')
  const [prevWinSize, setPrevWinSize] = useState({ width: 0, height: 0 }) // Used for checking if window size has changed
  const [openParents, setOpenParents] = useState([])
  const [focusedLabel, setFocusedLabel] = useState(-1) // Stores focused option label when using keyboard

  const selectRef = useRef(null)
  const optionsRef = useRef(null)
  const buttonsRef = useRef(null)

  const keyEvents = {} // Stores multiple keys being pressed

  const handleConfirmLocal = useCallback(() => {
    setIsOpen(false)
    setFocusedLabel(-1)
    handleConfirm([...selectedOptions])
  }, [handleConfirm, selectedOptions])

  useEffect(() => {
    if (optionsRef.current !== null) {
      let adjustHeight = false

      // Adjust dropdown position and height if it exceeds window boundaries
      if (window.outerWidth !== prevWinSize.width || window.innerHeight !== prevWinSize.height) {
        const currentOptionsRect = optionsRef.current.getBoundingClientRect()
        const parentLeft = selectRef.current.getBoundingClientRect().left
        const updatedRect = selectRect

        const selectHeight =
          initialHeight === 0 ? optionsRef.current.getBoundingClientRect().height : initialHeight

        if (window.innerHeight < selectHeight + 15 + currentOptionsRect.top) {
          updatedRect.height = window.innerHeight - currentOptionsRect.top - 15
          setSelectRect(updatedRect)
          adjustHeight = true
        } else {
          if (initialHeight === 0) {
            updatedRect.height = null
          } else {
            updatedRect.height = initialHeight
          }
          setSelectRect(updatedRect)
        }

        const horizontalAlignment =
          parentLeft + currentOptionsRect.width > window.outerWidth ? 'right' : 'left'
        setPlacement(horizontalAlignment)

        setPrevWinSize({
          width: window.outerWidth,
          height: window.innerHeight,
        })
      }

      if (initialHeight === 0) {
        setInitialHeight(optionsRef.current.getBoundingClientRect().height)

        if (!adjustHeight) {
          setSelectRect(optionsRef.current.getBoundingClientRect())
        }
      }
    }
  }, [isOpen, prevWinSize, selectRect, initialHeight])

  useEffect(() => {
    // set default to Hela sverige when no region is selected
    if (
      selectedOptions.length === 0 ||
      (selectedOptions.length === 1 &&
        (selectedOptions[0].value === 'ag=1' || selectedOptions[0].level === 2))
    ) {
      setSelectedOptions([{ label: texts.ALL_SWEDEN, value: '' }])
    }
    // set Alla områden as default when only one region is selected
    if (
      selectedOptions.length === 1 &&
      selectedOptions[0].level === 1 &&
      selectedOptions[0].value &&
      selectedOptions[0].value !== 'ag=1'
    ) {
      setSelectedOptions([
        ...selectedOptions,
        { label: texts.ALL_MUNICIPALITIES, value: queryString.stringify({ r }), level: 2 },
      ])
    }
  }, [selectedOptions, r])

  useEffect(() => {
    if (optionsRef.current !== null && focusedLabel !== -1) {
      optionsRef.current.querySelector('.focused').focus()
    }
  }, [focusedLabel])

  useEffect(() => {
    const checkIfOutside = (e) => {
      let target = e.relatedTarget
      if (target === null) {
        target = document.activeElement
      }

      if (
        target !== null &&
        typeof target !== 'undefined' &&
        selectRef &&
        selectRef.current !== null
      ) {
        if (!selectRef.current.contains(target) && e.target.textContent !== 'Rensa' && isOpen) {
          handleConfirmLocal()
        }
      }
    }

    document.addEventListener('click', checkIfOutside, false)
    return () => {
      document.removeEventListener('click', checkIfOutside, false)
    }
  }, [isOpen, handleConfirmLocal])

  const handleClear = () => {
    let newOptions = []
    if (topOptions[0].level === 2) {
      newOptions = selectedOptions.filter((option) => option.level === 1)
    }
    setSelectedOptions([...newOptions])
  }

  const onClickOption = (label, value, level) => {
    const matchedOptions = filter(
      selectedOptions,
      (option) => option.value === value && option.label === label,
    )

    // filter out options if they are already selected
    const filtermatchedOptions =
      matchedOptions.length >= 1
        ? selectedOptions.filter((option) => matchedOptions[0].value !== option.value)
        : selectedOptions

    const levelOneOptions = selectedOptions.filter((option) => option.level === 1 && option.value)

    if (matchedOptions.length >= 1) {
      setSelectedOptions([...filtermatchedOptions])
    } else {
      setSelectedOptions([...filtermatchedOptions, { label, value, level }])
    }

    if (level === 1) {
      // remove all selected options if Hela Sverige is selected and set Hela Sverige
      if (!value && label === texts.ALL_SWEDEN) {
        setSelectedOptions([{ label: texts.ALL_SWEDEN, value: '' }])
      } else if (matchedOptions.length === 0 && value) {
        // remove Hela sverige from the selectedOptions if some region is selected
        const filterHelaSverige = filtermatchedOptions.filter(
          (option) => option.label !== texts.ALL_SWEDEN,
        )
        setSelectedOptions([...filterHelaSverige, { label, value, level }])
      }
    }

    // remove all selected municipalities and subareas if two regions are selected
    const currentOptionAndLevelOneOptions = levelOneOptions.length === 1 && level === 1 && value
    if (currentOptionAndLevelOneOptions && levelOneOptions[0].value !== value) {
      setSelectedOptions([
        ...selectedOptions.filter((option) => option.level === 1),
        { label, value, level },
      ])
    }

    if (currentOptionAndLevelOneOptions && levelOneOptions[0].value === value) {
      setSelectedOptions([{ label: texts.ALL_SWEDEN, value: '' }])
    }

    if (level > 1) {
      const selectedMunicipalities = selectedOptions.filter(
        (option) => option.value.indexOf('m') > -1 && option.value.indexOf('sa') === -1,
      )

      const selectedSubAreas = selectedOptions.filter((option) => option.value.indexOf('sa') > -1)
      if (label === texts.ALL_MUNICIPALITIES) {
        // remove all selected municipalities and subareas if Alla områden is selected
        setSelectedOptions([
          ...levelOneOptions,
          { label: texts.ALL_MUNICIPALITIES, value: queryString.stringify({ r }) },
        ])
      } else if (matchedOptions.length === 0 && label !== texts.ALL_MUNICIPALITIES) {
        // remove Alla områden from the selectedOptions if some municiplaity or subarea is selected
        const filterAllaOmråden = filtermatchedOptions.filter(
          (option) => option.label !== texts.ALL_MUNICIPALITIES,
        )
        setSelectedOptions([...filterAllaOmråden, { label, value, level }])
      }
      if (
        selectedMunicipalities.length > 0 &&
        selectedSubAreas.length === 0 &&
        value.indexOf('sa') > -1
      ) {
        // remove selected municipalities if more than two sub areas are selected
        setSelectedOptions([...levelOneOptions, ...selectedSubAreas, { label, value, level }])
      } else if (
        selectedSubAreas.length > 0 &&
        selectedMunicipalities.length === 0 &&
        value.indexOf('m') > -1 &&
        value.indexOf('sa') === -1
      ) {
        // remove selected subareas if more than two sub areas are selected
        setSelectedOptions([...levelOneOptions, ...selectedMunicipalities, { label, value, level }])
      }
    }
  }

  const onParentToggle = (value) => {
    let currentOpenParents = openParents
    const index = currentOpenParents.indexOf(value)

    if (index !== -1) {
      currentOpenParents = currentOpenParents.filter((parent) => parent !== value)
    } else {
      currentOpenParents.push(value)
    }
    setOpenParents([...currentOpenParents])
  }

  const toggleDropDown = () => {
    if (!isOpen) {
      setIsOpen(true)
      setSelectedOptions(initialState)
    } else {
      handleConfirmLocal()
    }
  }

  const countAllTopOptions = () => {
    let topOptionsCount = topOptions.length
    if (openParents.length > 0) {
      topOptions.map((option) => {
        if (option.children !== null && typeof option.children !== 'undefined') {
          topOptionsCount += option.children.length
        }
        return topOptionsCount
      })
    }
    return topOptionsCount
  }

  // Keyboard event handling
  const handleKeyDown = (e) => {
    const _focusedLabel = focusedLabel

    keyEvents[e.key] = true

    switch (e.key) {
      case ' ':
      case 'Enter':
        if (focusedLabel === -1 && !e.target.classList.contains('option-toggler')) {
          if (isOpen && e.target.id === 'geopicker-confirm-button') {
            e.preventDefault()
            handleConfirmLocal()
          } else if (e.target.id !== 'geopicker-clear-button') {
            e.preventDefault()
            setIsOpen(!isOpen)
          }
        } else {
          e.preventDefault()
          let context = mergedOptions
          let offset = 0
          if (openParents.length > 0) {
            const topOptionsCount = countAllTopOptions()

            // Index can change if a parent option is opened, therefore we need to add an offset
            if (focusedLabel + 1 > topOptions.length && focusedLabel < topOptionsCount) {
              offset = topOptions.length
              context = topOptions[1].children
            }
            if (focusedLabel + 1 > topOptionsCount) {
              offset = topOptionsCount - topOptions.length
            }
          }

          let index = 0
          if (focusedLabel === -1) {
            // When screen reader is on we can't set focusedLabel so instead we need to fetch the index from activeElement
            index = document.activeElement.getAttribute('data-index')
          } else {
            index = focusedLabel - offset
          }

          const { label, value, level } = context[index]
          onClickOption(label, value, level)
        }
        break
      case 'Tab':
        if (isOpen) {
          if (!keyEvents.Shift) {
            e.preventDefault()
            if (document.activeElement.id !== 'geopicker-confirm-button') {
              // Skip to the buttons at the bottom if the user is navigating the list
              setFocusedLabel(-1)

              if (
                buttonsRef.current.querySelector('#geopicker-clear-button') !== null &&
                document.activeElement.id !== 'geopicker-clear-button' &&
                !buttonsRef.current.querySelector('#geopicker-clear-button').disabled
              ) {
                buttonsRef.current.querySelector('#geopicker-clear-button').focus()
              } else {
                buttonsRef.current.querySelector('#geopicker-confirm-button').focus()
              }
            } else {
              // Close the dropdown when tabbing away from the last button
              toggleDropDown()
            }
          } else {
            // Close the dropdown after reverse tabbing out of it
            if (document.activeElement === selectRef.current.querySelector('.selection')) {
              toggleDropDown()
            }

            // Reverse tabbing from the buttons should bring you back to the start of the dropdown
            if (
              document.activeElement.id === 'geopicker-clear-button' ||
              (document.activeElement.id === 'geopicker-confirm-button' &&
                buttonsRef.current.querySelector('#geopicker-clear-button').disabled) ||
              focusedLabel !== -1
            ) {
              e.preventDefault()
              selectRef.current.querySelector('.selection').focus()
            }
          }
        }
        break
      case 'Escape':
        if (isOpen) {
          handleConfirmLocal()
        }
        break
      case 'ArrowDown': {
        e.preventDefault()

        const topOptionsCount = countAllTopOptions()

        if (!isOpen) {
          setIsOpen(true)
        }
        if (
          focusedLabel < topOptionsCount + options.length - 1 &&
          document.activeElement.id !== 'geopicker-clear-button' &&
          document.activeElement.id !== 'geopicker-confirm-button'
        ) {
          setFocusedLabel(focusedLabel + 1)

          if (focusedLabel > topOptionsCount - 1) {
            const optionsParent = optionsRef.current.querySelector('.options')
            const optionsParentRect = optionsParent.getBoundingClientRect()
            const focusedLabelRect = optionsParent.childNodes[
              focusedLabel - topOptionsCount
            ].getBoundingClientRect()

            // Update the scroll position of the options list
            optionsParent.scrollTop =
              focusedLabelRect.top +
              optionsParent.scrollTop +
              focusedLabelRect.height -
              (optionsParentRect.height + optionsParentRect.top - focusedLabelRect.height)
          }
        }
        break
      }
      case 'ArrowUp': {
        e.preventDefault()

        const topOptionsCount = countAllTopOptions()

        if (focusedLabel > 0) {
          setFocusedLabel(_focusedLabel - 1)

          if (focusedLabel > topOptionsCount - 1) {
            const optionsParent = optionsRef.current.querySelector('.options')
            const optionsParentRect = optionsParent.getBoundingClientRect()
            const focusedLabelRect = optionsParent.childNodes[
              focusedLabel - topOptionsCount
            ].getBoundingClientRect()

            // Update the scroll position of the options list
            optionsParent.scrollTop =
              focusedLabelRect.top +
              optionsParent.scrollTop -
              (optionsParentRect.height + optionsParentRect.top)
          }
        }
        break
      }
      case 'ArrowRight':
        e.preventDefault()

        if (
          focusedLabel !== -1 &&
          typeof mergedOptions[focusedLabel].children !== 'undefined' &&
          mergedOptions[focusedLabel].children !== null
        ) {
          onParentToggle(mergedOptions[focusedLabel].value)
        }
        break
      case 'ArrowLeft':
        e.preventDefault()

        if (openParents.length > 0) {
          onParentToggle(mergedOptions[focusedLabel].value)
        }
        break
      default:
        // No default action
        break
    }
  }

  const handleKeyUp = (e) => {
    delete keyEvents[e.key]
  }

  const renderOption = (option, childOptions, index) => {
    const { label, value, children, level } = option

    const selected = find(
      selectedOptions,
      (selectedOption) => selectedOption.label === label && selectedOption.value === value,
    )

    const levelThreeOptions = selectedOptions.filter((opt) => opt.level === 3)

    const getLables =
      levelThreeOptions.length > 0 && levelThreeOptions.map((opt) => opt.label).join(', ')

    const isHelaSverigeSelected = some(
      selectedOptions,
      (opt) => opt.value === '' && opt.label === texts.ALL_SWEDEN,
    )
    const disabled = value === 'ag=1' && isHelaSverigeSelected

    const isOpenedParent = openParents.indexOf(value) !== -1

    let classes = 'option'
    if (selected) classes += ' selected'
    if (disabled) classes += ' disabled'
    if (children) classes += ' parent'
    if (children && isOpenedParent) classes += ' open'
    if (index === focusedLabel) classes += ' focused'

    return (
      <div key={label} className={classes}>
        <div className="option-inner">
          <li
            className="option-toggler"
            role="checkbox" // eslint-disable-line
            aria-checked={!!selected}
            aria-label={label}
            data-index={index}
            onClick={() => onClickOption(label, value, level)}
            onKeyPress={onEnterKeyEvent(() => onClickOption(label, value, level))}
            tabIndex="0"
          >
            <CheckBox
              className="option-checkbox"
              name={label}
              label={label}
              value={value}
              checked={!!selected}
              indeterminate={children && levelThreeOptions.length > 0}
              onClick={(e) => {
                e.preventDefault()
              }}
              disabled={disabled}
            />
            {children && !isOpenedParent && <StyledSubAreaLables>{getLables}</StyledSubAreaLables>}
          </li>
          {children && (
            <button
              className="option-parent-toggle"
              aria-label={`Välj stadsdel inom ${label}`}
              aria-haspopup="listbox"
              aria-expanded={isOpenedParent}
              onClick={() => onParentToggle(value)}
              onKeyPress={(e) => e.preventDefault}
              type="button"
              tabIndex="-1"
            >
              {isOpenedParent ? (
                <IconDown width="16px" height="16px" />
              ) : (
                <IconRight width="16px" height="16px" />
              )}
            </button>
          )}
        </div>
        {children && (
          <ul className="option-children" aria-label={`Stadsdelar inom ${label}`}>
            {childOptions}
          </ul>
        )}
      </div>
    )
  }

  const renderTop = () => {
    return (
      <div className="options-top">
        {topOptions.map((option, index) => {
          let _option = null

          if (option.children && openParents.length > 0) {
            const childOptions = option.children.map((childOption, childIndex) => {
              return renderOption(childOption, null, childIndex + topOptions.length)
            })
            _option = renderOption(option, childOptions, index)
          } else {
            _option = renderOption(option, null, index)
          }

          return _option
        })}
      </div>
    )
  }

  const renderConfirmBtn = () => {
    return (
      <StyledConfirmButton
        type="secondary"
        onClick={handleConfirmLocal}
        tabIndex="0"
        id="geopicker-confirm-button"
      >
        <StyledButtonText>Visa annonser</StyledButtonText>
      </StyledConfirmButton>
    )
  }

  const renderClearBtn = () => {
    const hasSelectedRegions =
      selectedOptions.filter((opt) => (opt.level === opt.value.indexOf('r=')) !== -1).length > 0 &&
      selectedOptions.length > 1
    const hasSelectedMunicipalities =
      selectedOptions.filter((opt) => opt.value.indexOf('m=') !== -1).length > 0
    const lvl = options[0].level

    const enabled = (lvl === 1 && hasSelectedRegions) || (lvl === 2 && hasSelectedMunicipalities)

    return (
      <StyledClearButton
        onClick={() => enabled && handleClear()}
        disabled={!enabled}
        tabIndex="0"
        id="geopicker-clear-button"
      >
        <StyledButtonText>Rensa</StyledButtonText>
      </StyledClearButton>
    )
  }

  const renderOptions = () => {
    if (!isOpen) {
      return null
    }

    let classes = 'dropdown'
    if (placement === 'right') classes += ' align-right'

    let topOptionsCount = topOptions.length
    if (topOptions) {
      if (openParents.length > 0) {
        topOptions
          .filter((option) => option.children !== null && typeof option.children !== 'undefined')
          .map((option) => {
            if (option.children !== null && typeof option.children !== 'undefined') {
              topOptionsCount += option.children.length
            }
            return topOptionsCount
          })
      }
    }

    return isArray(options) ? (
      <ul
        className={classes}
        ref={optionsRef}
        style={{ height: selectRect.height }}
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        tabIndex="0" // eslint-disable-line
      >
        <div className="options-wrapper">
          {topOptions && renderTop()}
          <div className="options">
            {options.map((option, index) => {
              return renderOption(option, null, index + topOptionsCount)
            })}
          </div>
        </div>
        <div ref={buttonsRef} className="action-buttons">
          {renderClearBtn()}
          {renderConfirmBtn()}
        </div>
      </ul>
    ) : null
  }

  return (
    <div className={`select${isOpen ? ' open' : ''}`} ref={selectRef}>
      <button
        className="selection"
        onKeyDown={handleKeyDown}
        onKeyUp={handleKeyUp}
        onClick={toggleDropDown}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        type="button"
      >
        <div className="placeholder">{placeholder}</div>
        {isOpen ? (
          <StyledIconUp width="16px" height="16px" />
        ) : (
          <StyledIconDown width="16px" height="16px" />
        )}
      </button>
      {renderOptions()}
    </div>
  )
}

MultiSelect.propTypes = {
  handleConfirm: PropTypes.func,
  placeholder: PropTypes.string,
  ariaLabel: PropTypes.string,
  topOptions: PropTypes.array,
  options: PropTypes.array,
  location: PropTypes.shape({
    search: PropTypes.string.isRequired,
  }).isRequired,
  allOptions: PropTypes.array,
  regionParameter: PropTypes.object.isRequired,
}
MultiSelect.defaultProps = {
  ariaLabel: '',
}

export default withRouter(MultiSelect)
